export type QuestionCategory =
  | 'lifestage'
  | 'age'
  | 'environment'
  | 'workstyle'
  | 'tendencies'
  | 'readiness'
  | 'constraints'
  | 'rejection'
  | 'tradeoff';

// 7 traits used by the v2 matching engine.
export type TraitKey =
  | 'analytical'
  | 'creative'
  | 'social'
  | 'physical'
  | 'structure'
  | 'risk'
  | 'income';

// Per-option deltas applied to the user's running trait totals.
export type TraitDelta = Partial<Record<TraitKey, number>>;

// '13-17' replaces the legacy 'under18' band. Kept in the union so any
// historical stored answers still type-check, but no UI option emits it.
export type AgeRange = '13-17' | 'under18' | '18-21' | '22-25' | '26-34' | '35-44' | '45plus';
export type Readiness = 'now' | '3-6m' | 'exploring' | 'curious';

// Filter signals an option can fire (consumed by careerFilter / scoring).
export interface OptionFilters {
  studyTolerance?: 'none' | 'short' | 'medium' | 'long';
  riskTolerance?: 'high' | 'medium' | 'low' | 'avoid';
  pressureTolerance?: 'high' | 'medium' | 'low' | 'avoid';
  securityNeed?: 'high' | 'medium' | 'low' | 'none';
  avoidsPhysical?: boolean;
  avoidsOutdoor?: boolean;
  needsRegularHours?: boolean;
  lifeStage?: LifeStage;
  ageRange?: AgeRange;
  readiness?: Readiness;
  rejectionTag?: RejectionTag;
  // Q22 trade-off (income vs enjoyment).
  tradeoff?: 'income' | 'enjoyment';
  // Q23 energy drain.
  energyDrain?: 'repetitive' | 'people' | 'sedentary' | 'chaos';
}

export interface QuestionOption {
  label: string;
  deltas?: TraitDelta;
  filters?: OptionFilters;
}

export interface Question {
  id: number;
  text: string;
  subtext?: string;
  category: QuestionCategory;
  options: QuestionOption[];
  multiSelect?: boolean;
  maxSelections?: number;
  minSelections?: number;
  layout?: 'standard' | 'tile' | 'chip';
}

// NOTE: 'sixthform' kept in the type so legacy DB answers still compile,
// but the Q0 UI now collapses GCSE + sixth form into a single 'school' option.
export type LifeStage =
  | 'school'
  | 'sixthform'
  | 'college'
  | 'university'
  | 'working'
  | 'between';

export type RejectionTag =
  | 'healthcare'
  | 'manual'
  | 'desk'
  | 'sales'
  | 'numbers'
  | 'creative';

// Stable question ids the rest of the code relies on.
// Adding new questions ALWAYS uses a new id - never renumber existing ones,
// because user answers are persisted by question id.
export const QUESTION_IDS = {
  lifeStage: 0,
  workday: 1, // Q1 'workday feels most natural' - referenced by scoring
  age: 25,
  readiness: 26,
} as const;

// =============================================================
// TonyKnows v2.2 - sharpened decision engine
// Display order is controlled by this array. Question.id is a stable
// data key (used in stored answers) - it is NOT the user-facing number.
// =============================================================

export const questions: Question[] = [
  // ---------- Q0 - Life Stage ----------
  {
    id: 0,
    category: 'lifestage',
    layout: 'tile',
    text: 'Where are you right now?',
    options: [
      { label: 'In school (GCSEs)',                                 filters: { lifeStage: 'school' } },
      { label: 'In school (A-levels / sixth form)',                 filters: { lifeStage: 'sixthform' } },
      { label: 'In further education (college or apprenticeship)', filters: { lifeStage: 'college' } },
      { label: 'At university',                                     filters: { lifeStage: 'university' } },
      { label: 'Working full-time',                                 filters: { lifeStage: 'working' } },
      { label: 'Not currently working or studying',                 filters: { lifeStage: 'between' } },
    ],
  },

  // ---------- Q1 - Age (NEW) ----------
  {
    id: 25,
    category: 'age',
    layout: 'chip',
    text: 'How old are you?',
    subtext: 'This service is for users aged 13 and over. We use this to suggest realistic paths and timelines.',
    options: [
      { label: '13-17',    filters: { ageRange: '13-17' } },
      { label: '18-21',    filters: { ageRange: '18-21' } },
      { label: '22-25',    filters: { ageRange: '22-25' } },
      { label: '26-34',    filters: { ageRange: '26-34' } },
      { label: '35-44',    filters: { ageRange: '35-44' } },
      { label: '45+',      filters: { ageRange: '45plus' } },
    ],
  },

  // ---------- Environment ----------
  {
    id: 1, category: 'environment',
    text: 'What kind of workday feels most natural to you?',
    options: [
      { label: 'Sitting and focusing deeply',          deltas: { analytical: 12, structure: 8 } },
      { label: 'Moving around and staying active',     deltas: { physical: 12, risk: 6 } },
      { label: 'A mix of both',                        deltas: { structure: 6, creative: 6 } },
      { label: 'Outdoors / constantly on the move',    deltas: { physical: 14, risk: 10 } },
    ],
  },
  {
    id: 2, category: 'environment',
    text: 'How do you feel about working in the same place most days?',
    options: [
      { label: 'I prefer consistency and routine',     deltas: { structure: 12 } },
      { label: 'I am happy either way',                deltas: { structure: 4, social: 4 } },
      { label: 'I prefer changing environments',       deltas: { risk: 10, creative: 8 } },
      { label: 'I want constant variety and movement', deltas: { risk: 14, structure: -10 } },
    ],
  },
  {
    id: 4, category: 'environment',
    text: 'How do you feel about physical work?',
    options: [
      { label: 'I enjoy physical work',              deltas: { physical: 14 } },
      { label: "I'm okay with some physical work",   deltas: { physical: 8 } },
      { label: 'I prefer mostly non-physical work',  deltas: { analytical: 10 } },
      { label: 'I want to avoid physical work',      deltas: { analytical: 12, physical: -20 }, filters: { avoidsPhysical: true } },
    ],
  },
  {
    id: 5, category: 'environment',
    text: 'What kind of routine suits you best?',
    options: [
      { label: 'Clear structure and predictability',           deltas: { structure: 14 } },
      { label: 'Some structure with flexibility',              deltas: { structure: 8 } },
      { label: 'Mostly flexible and self-directed',            deltas: { structure: -12, creative: 10 } },
      { label: 'No routine - I prefer every day to be different', deltas: { risk: 12, structure: -15 } },
    ],
  },

  // ---------- Work Style ----------
  {
    id: 6, category: 'workstyle',
    text: "When starting something new, what's your instinct?",
    options: [
      { label: 'Plan it out before starting',          deltas: { structure: 12 } },
      { label: 'Start quickly and adjust as I go',     deltas: { risk: 10 } },
      { label: 'Talk it through with others first',    deltas: { social: 12 } },
      { label: 'Try different approaches to see what works', deltas: { creative: 12, risk: 6 } },
    ],
  },
  {
    id: 7, category: 'workstyle',
    text: 'In a group, what role do you naturally take?',
    options: [
      { label: 'Leading and making decisions',            deltas: { social: 8, income: 12 } },
      { label: 'Supporting and contributing',             deltas: { social: 10 } },
      { label: 'Working independently',                   deltas: { analytical: 14, social: -10 } },
      { label: 'Organising and keeping things on track',  deltas: { structure: 12 } },
    ],
  },
  {
    id: 8, category: 'workstyle',
    text: 'How do you handle pressure?',
    options: [
      { label: 'I perform better under pressure',     deltas: { risk: 12 }, filters: { pressureTolerance: 'high' } },
      { label: 'I handle it fine',                    deltas: { structure: 4 }, filters: { pressureTolerance: 'medium' } },
      { label: 'I prefer moderate pressure',          deltas: { structure: 6 }, filters: { pressureTolerance: 'low' } },
      { label: 'I avoid high-pressure situations',    deltas: { structure: 10, risk: -10 }, filters: { pressureTolerance: 'avoid' } },
    ],
  },
  {
    id: 9, category: 'workstyle',
    text: 'What feels most satisfying?',
    options: [
      { label: 'Solving something difficult',         deltas: { analytical: 14 } },
      { label: 'Helping someone',                     deltas: { social: 14 } },
      { label: 'Creating something',                  deltas: { creative: 14 } },
      { label: 'Getting things done efficiently',     deltas: { physical: 12, structure: 6 } },
    ],
  },
  {
    id: 10, category: 'workstyle',
    text: "When something goes wrong, what's your first instinct?",
    options: [
      { label: 'Fix it immediately',          deltas: { physical: 12 } },
      { label: 'Step back and analyse it',    deltas: { analytical: 12 } },
      { label: 'Ask someone for help',        deltas: { social: 10 } },
      { label: 'Try a different approach',    deltas: { creative: 10, risk: 6 } },
    ],
  },

  // ---------- Natural Tendencies ----------
  {
    id: 11, category: 'tendencies',
    text: 'When you have no instructions, what do you naturally do?',
    options: [
      { label: 'Figure something out',     deltas: { analytical: 10 } },
      { label: 'Talk to someone',          deltas: { social: 10 } },
      { label: 'Make or create',           deltas: { creative: 12 } },
      { label: 'Fix or improve something', deltas: { physical: 12 } },
    ],
  },
  {
    id: 12, category: 'tendencies',
    text: 'What do people rely on you for most?',
    options: [
      { label: 'Solving problems',           deltas: { analytical: 12 } },
      { label: 'Supporting and helping them', deltas: { social: 12 } },
      { label: 'Coming up with ideas',       deltas: { creative: 12 } },
      { label: 'Getting things done',        deltas: { physical: 12 } },
    ],
  },
  {
    id: 13, category: 'tendencies',
    text: 'What kind of problems interest you most?',
    options: [
      { label: 'Logical / technical',     deltas: { analytical: 14 } },
      { label: 'People-related',          deltas: { social: 14 } },
      { label: 'Creative',                deltas: { creative: 14 } },
      { label: 'Real-world / practical',  deltas: { physical: 14 } },
    ],
  },
  {
    id: 14, category: 'tendencies',
    text: 'What kind of success matters most to you?',
    options: [
      { label: 'Becoming highly skilled at something', deltas: { analytical: 10 } },
      { label: 'Making a difference to people',        deltas: { social: 12 } },
      { label: 'Creating something meaningful',        deltas: { creative: 12 } },
      { label: 'Being reliable and dependable',        deltas: { structure: 12 } },
    ],
  },

  // ---------- Q15 - Career Readiness (NEW) ----------
  {
    id: 26, category: 'readiness', layout: 'tile',
    text: 'How ready are you to make a career move right now?',
    options: [
      { label: 'Ready to start immediately',           filters: { readiness: 'now' } },
      { label: 'Ready within the next 3-6 months',     filters: { readiness: '3-6m' } },
      { label: 'Still exploring options',              filters: { readiness: 'exploring' } },
      { label: 'Not ready yet, just curious',          filters: { readiness: 'curious' } },
    ],
  },

  // ---------- Constraints ----------
  {
    id: 16, category: 'constraints',
    text: 'How do you feel about studying for a career?',
    options: [
      { label: 'Happy to study 3-7+ years', filters: { studyTolerance: 'long' } },
      { label: 'Okay with 1-2 years',       filters: { studyTolerance: 'medium' } },
      { label: 'Prefer short training',     filters: { studyTolerance: 'short' } },
      { label: 'Want to start ASAP',        filters: { studyTolerance: 'none' } },
    ],
  },
  {
    id: 17, category: 'constraints',
    text: 'Which matters more to you right now?',
    options: [
      { label: 'Earning as much as possible',              deltas: { income: 18, risk: 12, analytical: 6 } },
      { label: 'A balance between money and lifestyle',    deltas: { structure: 4, social: 4 } },
      { label: 'Flexibility and lifestyle over money',     deltas: { structure: -14, creative: 8, income: -6 } },
    ],
  },
  {
    id: 24, category: 'constraints',
    text: 'How do you want your career to feel over time?',
    options: [
      { label: "Fast growth, even if it's competitive",   deltas: { risk: 10, analytical: 6 } },
      { label: 'Steady progress with more security',      deltas: { structure: 14, risk: -6 } },
    ],
  },
  {
    id: 18, category: 'constraints',
    text: 'How do you feel about job security?',
    options: [
      { label: 'Very important',     deltas: { structure: 8, risk: -10 }, filters: { securityNeed: 'high' } },
      { label: 'Somewhat important', filters: { securityNeed: 'medium' } },
      { label: 'Not important',      deltas: { risk: 8 }, filters: { securityNeed: 'low' } },
      { label: 'Not a priority',     deltas: { risk: 12 }, filters: { securityNeed: 'none' } },
    ],
  },
  {
    id: 19, category: 'constraints',
    text: 'How do you feel about risk?',
    options: [
      { label: 'I enjoy it',            deltas: { risk: 14 }, filters: { riskTolerance: 'high' } },
      { label: 'Comfortable with some', deltas: { risk: 8 }, filters: { riskTolerance: 'medium' } },
      { label: 'Prefer low risk',       deltas: { structure: 10, risk: -10 }, filters: { riskTolerance: 'low' } },
      { label: 'Avoid risk',            deltas: { structure: 14, risk: -20 }, filters: { riskTolerance: 'avoid' } },
    ],
  },
  {
    id: 20, category: 'constraints',
    text: 'What kind of career path suits you best?',
    options: [
      { label: 'Clear and structured',                 deltas: { structure: 14 } },
      { label: 'Some structure with flexibility',      deltas: { structure: 8 } },
      { label: 'Flexible and self-directed',           deltas: { creative: 8, structure: -8 } },
      { label: 'I prefer to figure things out as I go', deltas: { risk: 10, structure: -12 } },
    ],
  },

  // ---------- Q21 Rejection layer ----------
  {
    id: 21, category: 'rejection', layout: 'chip',
    multiSelect: true, maxSelections: 2, minSelections: 2,
    text: 'Which of these would you strongly dislike?',
    options: [
      { label: 'Healthcare or medical work',         filters: { rejectionTag: 'healthcare' } },
      { label: 'Physical or manual labour',          filters: { rejectionTag: 'manual' } },
      { label: 'Sitting at a desk most of the day',  filters: { rejectionTag: 'desk' } },
      { label: 'Sales or persuading people',         filters: { rejectionTag: 'sales' } },
      { label: 'Working with numbers or data',       filters: { rejectionTag: 'numbers' } },
      { label: 'Creative or artistic work',          filters: { rejectionTag: 'creative' } },
    ],
  },

  // ---------- Q22 Trade-off ----------
  {
    id: 22, category: 'tradeoff',
    text: 'If you had to choose one, what matters more?',
    options: [
      { label: 'Earning a lot of money',         deltas: { income: 25, structure: 5 }, filters: { tradeoff: 'income' } },
      { label: 'Enjoying your day-to-day work',  deltas: { creative: 10, social: 5, income: -10 }, filters: { tradeoff: 'enjoyment' } },
    ],
  },

  // ---------- Q23 Energy drain ----------
  {
    id: 23, category: 'tradeoff',
    text: 'What drains your energy the most?',
    options: [
      { label: 'Repetitive tasks',          deltas: { creative: 15, structure: -10 }, filters: { energyDrain: 'repetitive' } },
      { label: 'Dealing with people all day', deltas: { social: -20, analytical: 5 }, filters: { energyDrain: 'people' } },
      { label: 'Sitting still too long',    deltas: { physical: 15, analytical: -5 }, filters: { energyDrain: 'sedentary' } },
      { label: 'Unclear or chaotic work',   deltas: { structure: 20, risk: -10 }, filters: { energyDrain: 'chaos' } },
    ],
  },
];

// =============================================================
// Dynamic question wording for younger / school-stage users.
// We do NOT branch the questionnaire - we just rephrase a few prompts
// to feel less work-coded for 13-17 year olds and anyone "in school".
// =============================================================
const SCHOOL_STAGE_WORDING: Record<number, { text?: string; subtext?: string }> = {
  // Q1 - workday
  1: { text: 'What kind of day sounds most enjoyable to you?' },
  // Q14 - "success that matters" already neutral; no change.
  // Q9 - "What feels most satisfying" already neutral.
  // Q22 trade-off - keep.
  // Workstyle Q6 framed as "starting something new" - keep.
  // The Spec calls out: motivation + environment. Closest matches:
  //   Q17 "Which matters more right now" - keep (income vs lifestyle is fine for teens).
  //   Q5 "What kind of routine suits you best" stands in for "environment".
  5: { text: 'What kind of environment do you enjoy being in?' },
  // Add a teen-friendly motivation framing on Q14.
  14: { text: 'What kind of success matters most to you when learning or working on something?' },
};

/**
 * Returns the question text + subtext to display, optionally rephrased for
 * school-stage / 13-17 users. Pure function - safe to call from render.
 *
 * Trigger rules (per spec):
 *  - PRIMARY: ageRange === '13-17'
 *  - FALLBACK: lifeStage is school/sixthform AND ageRange is not yet set.
 *    Once an age IS set we trust it - we do NOT apply youth phrasing to a
 *    25-year-old who happens to be in college / sixth form.
 */
export function getQuestionDisplay(
  q: Question,
  ctx: { ageRange?: AgeRange; lifeStage?: LifeStage } = {},
): { text: string; subtext?: string } {
  const isTeen = ctx.ageRange === '13-17';
  const ageNotYetSet = ctx.ageRange == null;
  const isSchoolFallback =
    ageNotYetSet && (ctx.lifeStage === 'school' || ctx.lifeStage === 'sixthform');
  const isSchoolStage = isTeen || isSchoolFallback;
  if (!isSchoolStage) return { text: q.text, subtext: q.subtext };
  const override = SCHOOL_STAGE_WORDING[q.id];
  if (!override) return { text: q.text, subtext: q.subtext };
  return {
    text: override.text ?? q.text,
    subtext: override.subtext ?? q.subtext,
  };
}

// Section weights for trait aggregation (sum to 100, excluding lifestage/age/readiness).
export const SECTION_WEIGHT: Record<QuestionCategory, number> = {
  lifestage: 0,
  age: 0,
  environment: 15,
  workstyle: 20,
  tendencies: 12,
  readiness: 0,
  constraints: 33,
  rejection: 12,
  tradeoff: 8,
};

// Legacy exports kept so old imports compile.
export type RecentExperience = 'studying' | 'working' | 'trying' | 'building' | 'unsure';
export const lifeStageMap: LifeStage[] = ['school', 'sixthform', 'college', 'university', 'working', 'between'];
export const recentExperienceMap: RecentExperience[] = ['studying', 'working', 'trying', 'building', 'unsure'];
